import { Button } from "Components/Button";
import ConfirmAlert from "Components/Alert/ConfirmAlert";
import { useState, useEffect } from "react";
import { Box } from "styles/globalComponents";
import { useParams, useHistory } from "react-router";
import { createToast } from "Utils/toastFunc";
import {
  SavePreBudgetData,
  UpdatePreOrderUnitData,
  updatePreOrderUnit,
  sendPreOrderToMotion,
  deletePreBudgetUnit,
} from "Requests/Partners/PreBudgets";
import LoadingPurple from "Assets/Images/loading-purple.gif";
import { FormikProvider, useFormik } from "formik";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useItemsContext } from "../../hooks/useItemsContext";
import { InfoDetails, PersonInfo, Values, personData } from "./InfoDetails";
import { CartSummary } from "./CartDetails/CartSummary";

function mapGenderFromApi(value?: string | null): string {
  if (!value) return "";
  const normalized = value.toLowerCase();
  if (normalized === "m" || normalized === "masculino") return "M";
  if (normalized === "f" || normalized === "feminino") return "F";
  return "";
}

function normalizeBirthDateForInput(raw?: string | null): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (trimmed.includes(" ")) return trimmed.split(" ")[0].slice(0, 10);
  return trimmed.slice(0, 10);
}

function omitUnitId<T extends { unit_id: string }>(row: T): Omit<T, "unit_id"> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omit for API payload shape
  const { unit_id, ...rest } = row;
  return rest;
}

/** Converte convênio vazio em null para o payload da API. */
function emptyInsuranceToNull(value: string | undefined | null): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
}

function patientWithInsuranceNullsForApi(
  patient: SavePreBudgetData["patient"]
): SavePreBudgetData["patient"] {
  return {
    ...patient,
    gender: patient.gender?.toUpperCase(),
    insurance_operator: emptyInsuranceToNull(
      patient.insurance_operator ?? null
    ),
    health_insurance_card_number: emptyInsuranceToNull(
      patient.health_insurance_card_number ?? null
    ),
    have_health_insurance: emptyInsuranceToNull(
      patient.have_health_insurance ?? null
    ),
  };
}

interface CartDetailsProps {
  blockInteraction: boolean;
  setPageBlocked: (status: boolean) => void;
  patientData?: {
    full_name?: string;
    document?: string;
    phone?: string;
    ddd?: string | null;
    ddi?: string | null;
    email?: string | null;
    doctor?: string | null;
    crm?: string | null;
    mothers_full_name?: string | null;
    birth_date?: string | null;
    insurance_operator?: string | null;
    health_insurance_card_number?: string | null;
    have_health_insurance?: string | null;
    health_files?: { file_path: string }[];
    gender?: string | null;
    unit_id?: number | null;
  };
  iaResponse: {
    consolidated_analysis?: {
      ai_analysis?: {
        requestingDoctor?: string;
        doctorCRM?: string;
      };
    };
  } | null;
}

export const CartDetails = ({
  blockInteraction,
  setPageBlocked,
  patientData,
  iaResponse,
}: CartDetailsProps) => {
  const [tab, setTab] = useState<"cart" | "info">("cart");
  const [loading, setLoading] = useState(false);

  const { items, clearCart, getTrackHistory, getNewTrackHistory } =
    useItemsContext();

  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  // Estado do formulário movido para o componente pai
  const [formInitialized, setFormInitialized] = useState(false);
  const [formValues, setFormValues] = useState<Values>({
    name: "",
    document: "",
    phone: "",
    email: "",
    birth_date: "",
    gender: "",
    doctor: "",
    crm: "",
    mothers_full_name: "",
    insurance_operator: "",
    health_insurance_card_number: "",
    have_health_insurance: "",
    unit_id: "",
  });

  const [forceEditMode, setForceEditMode] = useState(false);

  const handleSubmit = async (values?: PersonInfo) => {
    if (!id || items.length === 0) {
      createToast("warning", "Não há alterações para salvar.");
      return;
    }

    if (!values || typeof values !== "object" || !("full_name" in values)) {
      setTab("info");
      return;
    }

    try {
      setPageBlocked(true);
      setLoading(true);

      const cartData = items.map((item) => ({
        product_id: item.product_id,
        amount: item.amount,
      }));

      let dddPhone = "";
      let ddiPhone = "";
      let phone = "";

      if (values?.phone) {
        const [ddi, ddd, n1, n2] = formatPhoneNumberIntl(values?.phone).split(
          " "
        );

        dddPhone = ddd;
        ddiPhone = ddi.replace("+", "");
        phone = `${n1}${n2}`;
      }

      const patientDataToSave = values || {
        full_name: patientData?.full_name || "Não informado",
        email: patientData?.email || "Não informado",
        doctor: patientData?.doctor || "Não informado",
        crm: patientData?.crm || "",
        document: patientData?.document || "",
        mothers_full_name: patientData?.mothers_full_name || "",
        birth_date: normalizeBirthDateForInput(patientData?.birth_date),
        gender: mapGenderFromApi(patientData?.gender).toLocaleUpperCase(),
        insurance_operator: patientData?.insurance_operator || "",
        health_insurance_card_number:
          patientData?.health_insurance_card_number || "",
        have_health_insurance: patientData?.have_health_insurance || "",
        unit_id:
          patientData?.unit_id != null ? String(patientData.unit_id) : "",
        ddd: dddPhone ?? "",
        ddi: ddiPhone ?? "",
      };

      const trackData = getTrackHistory();
      const newTrackData = getNewTrackHistory();

      const formattedTrackData =
        newTrackData ||
        trackData.map((trackItem) => ({
          produto_original: {
            produto_original_nome:
              trackItem.produto_original.produto_original_nome,
            produto_original_match_nome:
              trackItem.produto_original.produto_original_match_nome,
            produto_original_match_id:
              trackItem.produto_original.produto_original_match_id,
          },
          items_corretos: trackItem.items_corretos || [],
        }));

      const unitIdNumber = Number(values.unit_id);

      const patientPayload = patientWithInsuranceNullsForApi(
        omitUnitId(patientDataToSave) as SavePreBudgetData["patient"]
      );

      const saveData: UpdatePreOrderUnitData = {
        cart: cartData,
        patient: { ...patientPayload, phone, ddd: dddPhone, ddi: ddiPhone },
        track: formattedTrackData,
        unit_id: unitIdNumber,
      };

      await updatePreOrderUnit(Number(id), saveData);

      await sendPreOrderToMotion(Number(id));

      createToast("success", "Orçamento criado com sucesso!");

      history.push("/pre-pedido-unidade");
    } catch (error: unknown) {
      createToast("error", "Erro ao converter em orçamento. Tente novamente.");
    } finally {
      setPageBlocked(false);
      setLoading(false);
    }
  };

  const handleFormSubmit = (values: Values) => {
    const [n1, n2] = formatPhoneNumberIntl(values.phone).split(" ");

    const patient = {
      full_name: values.name,
      email: values.email || "",
      doctor: values.doctor || "",
      crm: values.crm || "",
      document: values.document || "",
      mothers_full_name: values.mothers_full_name || "",
      birth_date: values.birth_date || "",
      gender: values.gender.toUpperCase() || "",
      insurance_operator: emptyInsuranceToNull(values.insurance_operator),
      health_insurance_card_number: emptyInsuranceToNull(
        values.health_insurance_card_number
      ),
      have_health_insurance: emptyInsuranceToNull(values.have_health_insurance),
      unit_id: values.unit_id,
      phone: `${n1}${n2}`,
    };

    setForceEditMode(false);

    handleSubmit(patient);
  };

  const formik = useFormik({
    initialValues: formValues,
    onSubmit: handleFormSubmit,
    enableReinitialize: false,
    validationSchema: personData,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handleCancelPreBudget = async () => {
    try {
      setPageBlocked(true);
      setLoading(true);

      await deletePreBudgetUnit(Number(id));

      createToast("success", "Pré-orçamento cancelado com sucesso!");
      history.push("/pre-pedido-unidade/");
    } catch {
      createToast("error", "Não foi possível cancelar o pré-orçamento");
    } finally {
      setPageBlocked(false);
      setLoading(false);
    }
  };

  const handleClearCart = () => {
    clearCart();
    createToast("success", "Carrinho limpo com sucesso!");
  };

  const applyFormValidationErrors = (
    validationResult: Record<string, unknown>
  ) => {
    setForceEditMode(true);
    setTab("info");

    const touchedFields: Record<string, boolean> = {};
    Object.keys(validationResult).forEach((fieldName) => {
      touchedFields[fieldName] = true;
    });
    formik.setTouched(touchedFields);

    createToast(
      "warning",
      "Por favor, corrija os erros no formulário antes de continuar."
    );
  };

  const buildPatientFromForm = (): PersonInfo => {
    return {
      full_name: formik.values.name,
      email: formik.values.email || "",
      phone: formik.values.phone,
      doctor: formik.values.doctor || "",
      crm: formik.values.crm || "",
      document: formik.values.document || "",
      mothers_full_name: formik.values.mothers_full_name || "",
      birth_date: formik.values.birth_date || "",
      gender: formik.values.gender.toLowerCase() || "",
      insurance_operator: emptyInsuranceToNull(
        formik.values.insurance_operator
      ),
      health_insurance_card_number: emptyInsuranceToNull(
        formik.values.health_insurance_card_number
      ),
      have_health_insurance: emptyInsuranceToNull(
        formik.values.have_health_insurance
      ),
      unit_id: formik.values.unit_id,
    };
  };

  const convertConfirmLayout = (
    _: unknown,
    onClose: () => void,
    onSubmit: () => void
  ) => (
    <div className="custom-ui">
      <h5 className="h5 text-gray text-light">
        Deseja converter este pré-orçamento em orçamento?
      </h5>
      <div className="wrapper-buttons">
        <button type="button" className="link link-danger" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="button _action"
          onClick={() => {
            onClose();
            onSubmit();
          }}
        >
          Confirmar
        </button>
      </div>
    </div>
  );

  const cancelConfirmLayout = (
    _: unknown,
    onClose: () => void,
    onSubmit: () => void
  ) => (
    <div className="custom-ui">
      <h5 className="h5 text-gray text-light">
        Tem certeza que deseja excluir este pré-orçamento? Esta ação não pode
        ser desfeita.
      </h5>
      <div className="wrapper-buttons">
        <button type="button" className="link link-danger" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="button _action"
          onClick={() => {
            onClose();
            onSubmit();
          }}
        >
          Excluir
        </button>
      </div>
    </div>
  );

  const handleConvertToBudgetClick = async () => {
    const validationResult = await formik.validateForm();

    if (Object.keys(validationResult).length > 0) {
      applyFormValidationErrors(validationResult);
      return;
    }

    ConfirmAlert({ data: null }, convertConfirmLayout, () => {
      handleSubmit(buildPatientFromForm()).catch((error: unknown) => {
        console.error(error);
      });
    });
  };

  const handleDeletePreBudgetClick = () => {
    ConfirmAlert({ data: null }, cancelConfirmLayout, () => {
      handleCancelPreBudget().catch((error: unknown) => {
        console.error(error);
      });
    });
  };

  useEffect(() => {
    const handleRedirectToInfo = () => {
      setTab("info");
    };

    window.addEventListener("redirectToInfoTab", handleRedirectToInfo);

    return () => {
      window.removeEventListener("redirectToInfoTab", handleRedirectToInfo);
    };
  }, []);

  useEffect(() => {
    if (patientData && !formInitialized) {
      const { ddd, ddi, phone } = patientData;

      let customDdi = "55";

      if (ddi) {
        customDdi = ddi.replace(/\D/g, "");
      }

      const initialValues: Values = {
        name: patientData.full_name || "",
        document: patientData.document || "",
        phone: `+${customDdi} ${ddd} ${phone}` || "",
        email: patientData.email || "",
        doctor: patientData.doctor || "",
        crm: patientData.crm || "",
        mothers_full_name: patientData.mothers_full_name || "",
        birth_date: normalizeBirthDateForInput(patientData.birth_date),
        gender: mapGenderFromApi(patientData.gender).toLowerCase(),
        insurance_operator: patientData.insurance_operator || "",
        health_insurance_card_number:
          patientData.health_insurance_card_number || "",
        have_health_insurance: patientData.have_health_insurance || "",
        unit_id: patientData.unit_id != null ? String(patientData.unit_id) : "",
      };
      setFormValues(initialValues);
      setFormInitialized(true);
    }
  }, [patientData, formInitialized]);

  useEffect(() => {
    if (formInitialized && !formik.values.name) {
      formik.setValues(formValues);
    }
  }, [formInitialized]);

  useEffect(() => {
    if (iaResponse && formInitialized && formik.values.name) {
      const updates: Partial<Values> = {};

      if (
        iaResponse.consolidated_analysis?.ai_analysis?.requestingDoctor &&
        !formik.values.doctor
      ) {
        updates.doctor =
          iaResponse.consolidated_analysis.ai_analysis.requestingDoctor;
      }

      if (
        iaResponse.consolidated_analysis?.ai_analysis?.doctorCRM &&
        !formik.values.crm
      ) {
        updates.crm = iaResponse.consolidated_analysis.ai_analysis.doctorCRM;
      }

      if (Object.keys(updates).length > 0) {
        const newFormValues = { ...formik.values, ...updates };
        formik.setValues(newFormValues);
      }
    }
  }, [iaResponse, formInitialized]);

  return (
    <>
      <div
        className={`cart-information-resume ${
          blockInteraction ? "blocked" : ""
        }`}
      >
        <div className="tabs">
          <Button
            classButton={tab === "cart" ? "active" : ""}
            onClick={() => setTab("cart")}
            disabled={blockInteraction}
          >
            Carrinho
          </Button>
          <Button
            classButton={tab === "info" ? "active" : ""}
            onClick={() => setTab("info")}
            disabled={blockInteraction}
          >
            Dados
          </Button>
        </div>

        <div className="cart-details-container">
          {tab === "cart" && <CartSummary />}

          {tab === "info" && (
            <FormikProvider value={formik}>
              <InfoDetails
                blockUserInteraction={blockInteraction}
                forceEditMode={forceEditMode}
                onEditModeChange={setForceEditMode}
                registeredHealthFilesCount={
                  patientData?.health_files?.length ?? 0
                }
                iaData={{
                  requestingDoctor:
                    iaResponse?.consolidated_analysis?.ai_analysis
                      ?.requestingDoctor,
                  doctorCRM:
                    iaResponse?.consolidated_analysis?.ai_analysis?.doctorCRM,
                }}
              />
            </FormikProvider>
          )}
        </div>

        <Box className="cart-buttons">
          <Button
            onClick={handleConvertToBudgetClick}
            disabled={blockInteraction}
            classButton={`convert-button ${loading ? "loading-button" : ""}`}
          >
            {loading ? (
              <img src={LoadingPurple} alt="" className="gif" />
            ) : (
              "Enviar ao motion"
            )}
          </Button>

          <Button
            outlined
            onClick={handleDeletePreBudgetClick}
            disabled={blockInteraction}
            classButton={`delete-button ${loading ? "loading-button" : ""}`}
          >
            {loading ? (
              <img src={LoadingPurple} alt="" className="gif" />
            ) : (
              "Excluir pré-pedido"
            )}
          </Button>
          <Button
            outlined
            onClick={handleClearCart}
            disabled={blockInteraction || items.length === 0}
            classButton={`clear-button ${loading ? "loading-button" : ""}`}
          >
            Limpar carrinho
          </Button>
        </Box>
      </div>

      <style>{`
      .cart-buttons .loading-button {
        position: relative;
        padding: 0;
        width: auto;
        pointer-events: none;
        opacity: 0.6;
      }

      .cart-buttons button .gif {
        position: absolute;
        top: 0px;
        bottom: 0px;
        right: 0;
        left: 0;
        margin: auto;
        display: block;
        width: 36px;
        height: 36px;
      }
    `}</style>
    </>
  );
};
